import { faEye } from "@fortawesome/pro-solid-svg-icons"
import { Box, LinearProgress, Tooltip, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { getFormattedFileSize } from "app/tools/file"
import { dateSVC } from "app/util/date/service"
import {
  FILTER_MEDIA_APPROVAL_EXPANDED_HEIGHT_IN_REM,
  FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM,
  IMediaApprovalData,
  MEDIA_APPROVAL_STATUSES,
  MEDIA_SIMPLE_TYPES
} from "media/model"
import { getPreviewUrl, loadAllMediaApprovals } from "media/tools/media-approval"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { ListIdCell } from "ui/components/ListIdCell"
import { ListPreview } from "ui/components/ListPreview"
import { ListActionButton } from "ui/components/main-layout/ListActionButton"
import { getDataGridTexts } from "ui/tools/list"
import { MediaApprovalPopup } from "./MediaApprovalPopup"
import { MediaApprovalStatus } from "./MediaApprovalStatus"
import { MediaType } from "./MediaType"

interface IMediaApprovalListProps {
  mediaApprovalsData: IMediaApprovalData[]
  isLoading: boolean
  hasError: boolean
  isFilterExpanded: boolean
  isUpdating: boolean
}

const MediaApprovalListFn = React.memo(
  ({ mediaApprovalsData, isLoading, isFilterExpanded, isUpdating, hasError }: IMediaApprovalListProps): JSX.Element => {
    const [selectedMediaApprovalData, setSelectedMediaApprovalData] = React.useState<IMediaApprovalData | null>(null)
    React.useEffect(() => {
      loadAllMediaApprovals()
    }, [])
    const columns: Array<GridColDef<IMediaApprovalData>> = [
      {
        field: "preview",
        headerName: translate("label.preview"),
        align: "left",
        minWidth: 110,
        flex: 1,
        sortable: false,
        renderCell: params => (
          <ListPreview
            id={params.row.media_id}
            url={getPreviewUrl(params.row.media_id)}
            width={3.1}
            height={1.8}
            onClick={() => (isUpdating ? undefined : setSelectedMediaApprovalData(params.row))}
          />
        )
      },
      {
        field: "name",
        headerName: translate("label.name"),
        minWidth: 180,
        flex: 3,
        renderCell: params => (
          <ListIdCell
            label={params.row.name || "-"}
            id={`${params.row.media_id} - ${params.row.name}`}
            onClick={() => (isUpdating ? undefined : setSelectedMediaApprovalData(params.row))}
          />
        )
      },
      {
        field: "status",
        headerName: translate("label.status"),
        align: "left",
        minWidth: 110,
        flex: 1,
        renderCell: params => {
          let allStatuses = params.row.status.split(",").map(s => s.trim())
          return (
            <FlexBox vertical ai="start">
              {allStatuses.map((s, index) => (
                <MediaApprovalStatus key={index} status={s as MEDIA_APPROVAL_STATUSES} />
              ))}
            </FlexBox>
          )
        }
      },
      {
        field: "type",
        headerName: translate("label.type"),
        flex: 1,
        renderCell: params => <MediaType type={params.row.type} />
      },
      {
        field: "filesize",
        headerName: translate("label.size"),
        flex: 1,
        renderCell: params => getFormattedFileSize(params.row.filesize, 2)
      },
      {
        field: "bitrate",
        headerName: translate("label.bitrate"),
        flex: 1,
        renderCell: params => (params.row.type === MEDIA_SIMPLE_TYPES.VIDEO ? `${params.row.bitrate}kbps` : "-")
      },
      {
        field: "width",
        headerName: translate("label.width"),
        flex: 1
      },
      {
        field: "height",
        headerName: translate("label.height"),
        flex: 1
      },
      {
        field: "aspect_ratio",
        headerName: translate("label.aspect-ratio"),
        flex: 1,
        renderCell: params => params.row.aspect_ratio.toFixed(2)
      },
      {
        field: "advertiser_name",
        headerName: translate("label.advertiser"),
        minWidth: 90,
        flex: 2,
        renderCell: params => {
          if (!params.row.advertiser_name) {
            return "-"
          }
          return (
            <Tooltip title={params.row.advertiser_name}>
              <Typography color="textPrimary" fontSize=".8rem">
                {params.row.advertiser_name}
              </Typography>
            </Tooltip>
          )
        }
      },
      {
        field: "category",
        headerName: translate("label.category"),
        minWidth: 90,
        flex: 2,
        renderCell: params => {
          if (!params.row.category) {
            return "-"
          }
          return (
            <Tooltip title={params.row.category}>
              <Typography color="textPrimary" fontSize=".8rem">
                {params.row.category}
              </Typography>
            </Tooltip>
          )
        }
      },
      {
        field: "language",
        headerName: translate("label.language"),
        flex: 1,
        renderCell: params => params.row.language.toUpperCase()
      },
      {
        field: "created_at",
        headerName: translate("label.creationDate"),
        minWidth: 100,
        flex: 1,
        renderCell: params => (params.row.created_at ? dateSVC.format(params.row.created_at) : "")
      },
      {
        field: "actions",
        headerName: translate("label.actions"),
        sortable: false,
        flex: 1,
        renderCell: params => (
          <ListActionButton
            icon={faEye}
            disabled={isUpdating}
            tooltip={translate("media-approval.view.tooltip.title")}
            onClick={() => setSelectedMediaApprovalData(params.row)}
          />
        )
      }
    ]
    if (hasError) {
      return (
        <Typography color="error" fontSize=".9rem">
          {translate("label.error.unexpected")}
        </Typography>
      )
    } else if (isLoading) {
      return <LinearProgress sx={{ color: theme => theme.palette.primary.main, width: "100%" }} />
    }
    return (
      <Box
        id="media-approval-list"
        sx={{
          "position": "relative",
          "width": "100%",
          "minWidth": "100%",
          "maxWidth": "100%",
          "display": "flex",
          "flexDirection": "column",
          "alignItems": "center",
          "justifyContent": "center",
          "pl": ".4rem",
          "pr": ".4rem",
          "backgroundColor": theme => theme.palette.background.paper,
          "height": isFilterExpanded
            ? `calc(100% - ${FILTER_MEDIA_APPROVAL_EXPANDED_HEIGHT_IN_REM}rem - 6rem)`
            : `calc(100% - ${FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM}rem - 6rem)`,
          "minHeight": isFilterExpanded
            ? `calc(100% - ${FILTER_MEDIA_APPROVAL_EXPANDED_HEIGHT_IN_REM}rem - 6rem)`
            : `calc(100% - ${FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM}rem - 6rem)`,
          "maxHeight": isFilterExpanded
            ? `calc(100% - ${FILTER_MEDIA_APPROVAL_EXPANDED_HEIGHT_IN_REM}rem - 6rem)`
            : `calc(100% - ${FILTER_MEDIA_APPROVAL_HEADER_HEIGHT_IN_REM}rem - 6rem)`,
          "transition": "all 400ms ease",
          "& > *": {
            width: "100%"
          },
          "& .MuiDataGrid-root": {
            border: "none"
          },
          "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd)": {
            backgroundColor: theme => theme.palette.background.default
          },
          "& .MuiDataGrid-virtualScrollerRenderZone > div:nth-of-type(odd):hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)"
          },
          "& .MuiDataGrid-cell": {
            fontSize: ".8rem",
            fontWeight: 300
          }
        }}>
        <DataGrid
          rows={mediaApprovalsData}
          columns={columns}
          loading={isLoading}
          getRowId={row => row.media_id}
          className="media-approval-list"
          scrollbarSize={1}
          autoPageSize
          localeText={getDataGridTexts()}
          sx={{
            height: "100%",
            minHeight: "100%",
            maxHeight: "100%",
            overflow: "auto"
          }}
        />
        {selectedMediaApprovalData && (
          <MediaApprovalPopup
            mediaApprovalDatas={mediaApprovalsData}
            defaultMediaApprovalData={selectedMediaApprovalData}
            onClose={() => setSelectedMediaApprovalData(null)}
          />
        )}
      </Box>
    )
  }
)

export const MediaApprovalList = connect(
  (state: IRootState): IMediaApprovalListProps => ({
    mediaApprovalsData: state.media.sspMediaApproval.filteredMediaApprovalsData,
    isLoading: state.media.sspMediaApproval.isLoading,
    hasError: state.media.sspMediaApproval.hasError,
    isFilterExpanded: state.media.sspMediaApproval.isFilterExpanded,
    isUpdating: state.media.sspMediaApproval.isUpdating
  })
)(MediaApprovalListFn)
